import React from 'react';
import { useTheme } from '@mui/material';

interface ScenarioChartInput {
    data: { label: string; value: number }[];
    color: string;
    maxValue?: number;
    stepSize?: number;
    loading?: boolean;
}

export function useScenarioChartData({
    data,
    color,
    maxValue,
    stepSize,
    loading = false
}: ScenarioChartInput) {
    const theme = useTheme();

    const defaultMaxValue = Math.max(...data.map(d => d.value)) + Math.max(...data.map(d => d.value));
    const defaultStepSize = Math.ceil(defaultMaxValue / 10);

    const chartData = React.useMemo(
        () => ({
            labels: data.map(d => d.label),
            datasets: [
                {
                    data: data.map(d => (d.value === 0 ? null : d.value)),
                    backgroundColor: color,
                    borderRadius: {
                        topRight: 4,
                        bottomRight: 4
                    },
                    borderSkipped: false,
                    barThickness: 24
                }
            ]
        }),
        [data, color]
    );

    const chartOptions = React.useMemo(
        () => ({
            indexAxis: 'y' as const,
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: true,
                    backgroundColor: theme.palette.background.paper,
                    titleColor: theme.palette.text.primary,
                    bodyColor: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    borderWidth: 1,
                    padding: 12
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    max: maxValue ?? defaultMaxValue,
                    grid: { color: theme.palette.divider, borderDash: [3, 3] },
                    border: { display: false, dash: [3, 3] },
                    ticks: {
                        font: { size: 11 },
                        color: theme.palette.text.secondary,
                        stepSize: stepSize ?? defaultStepSize
                    }
                },
                y: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                        font: { size: 12, weight: 500 as const },
                        color: theme.palette.text.secondary
                    }
                }
            }
        }),
        [theme, maxValue, stepSize, defaultMaxValue, defaultStepSize]
    );

    return { chartData, chartOptions, loading };
}
